import React from 'react';
import { connect } from 'react-redux';

import { Impression } from '@nerdwallet/analytics/react/impression';
import { Tracking } from '@nerdwallet/nw-client-lib';
import lazy, { Hydrate } from '../../../lazy/lazy-universal';

import type {
  LoanTerm,
  Schedule,
  Halfway,
} from '../../../amortization-calculator/types';
import Breakdown from '../../../breakdown/breakdown';
import MonthlyPayment from '../../../monthly-payment/monthly-payment';
import { MORTGAGE_ANALYTICS_HASH } from '../analytics-constants';

// MRT_CTA_TRACKING_FORM_NAME_HASH,
import type { MortgageCalculators } from '../types';

// Lazy load components (Tab Content)
const Amortization = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-amortization' */ '../../../amortization-chart/amortization-chart'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const CompareLoanTypes = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-compare-loan-types' */ '../../../compare-loan-types'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const MrtCta = lazy(
  () => import(/* webpackChunkName: 'lazy-mrt-cta' */ '../mrt-cta/mrt-cta'),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

interface MCBreakdownProps {
  /** Callback to be called when selected tab changes */
  onSectionEnter?: (index: number) => void;
  /**
   * Tracking form name on CompareLoans Table view changed
   */
  trackingFormName: 'mc-results' | 'mpc-results';
  className?: string;
  mortgageCalculator: MortgageCalculators;
}

interface StateProps {
  /** from redux */
  loanTerm: LoanTerm;
  /** from redux */
  schedule: Schedule;
  /** from redux */
  halfway: Halfway;
}

type OwnProps = StateProps & MCBreakdownProps;

const trackSection = (trackingFormName: string, fieldValue: string) => {
  Tracking.formOpen({
    formName: trackingFormName,
    fieldName: 'results-section',
    fieldValue,
  });
};

const MCBreakdown = ({
  onSectionEnter,
  className,
  trackingFormName,
  mortgageCalculator,
  loanTerm,
  schedule,
  halfway,
}: OwnProps): JSX.Element => {
  const onTableViewChanged = (expanded: boolean) => {
    const fieldValue = expanded
      ? 'Compare loan details'
      : 'Compare common loan types';
    trackSection(trackingFormName, fieldValue);
  };

  const MONTHLY_PAYMENT_ANALYTICS =
    MORTGAGE_ANALYTICS_HASH[mortgageCalculator].MONTHLY_PAYMENT;

  const content = [
    {
      label: 'Monthly payment',
      hideLabelOnMobile: true,
      content: (
        <Impression
          eventType={MONTHLY_PAYMENT_ANALYTICS.EVENT_TYPE}
          entityName={MONTHLY_PAYMENT_ANALYTICS.ENTITY_NAME}
          payload={{
            form_name: MONTHLY_PAYMENT_ANALYTICS.FORM_NAME,
            section_name: MONTHLY_PAYMENT_ANALYTICS.SECTION_NAME,
            section_position: MONTHLY_PAYMENT_ANALYTICS.SECTION_POSITION,
          }}
        >
          <MonthlyPayment calculator="mortgage" />
          <MrtCta trackingFormName={mortgageCalculator} />
        </Impression>
      ),
    },
    {
      label: 'Compare common loan types',
      content: <CompareLoanTypes onTableViewChanged={onTableViewChanged} />,
    },
    {
      label: 'Amortization',
      content: (
        <Amortization
          loanTerm={loanTerm}
          schedule={schedule}
          halfway={halfway}
        />
      ),
    },
  ];

  return (
    <Breakdown
      className={className}
      content={content}
      onSectionEnter={onSectionEnter}
    />
  );
};

const mapStateToProps = (state) => {
  const reducer = state?.mortgageCalculator;

  return {
    loanTerm: reducer?.inputs?.loanTerm,
    schedule: reducer?.outputs?.schedule,
    halfway: reducer?.outputs?.halfway,
  };
};

export default connect<StateProps, Record<string, never>, MCBreakdownProps>(
  mapStateToProps
)(MCBreakdown);
